import { useState, useEffect } from 'react';
import { useData } from '../../../contexts/DataContext';
import { useLocation, useNavigate } from 'react-router-dom';

const Plans = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const queryParams = new URLSearchParams(location.search);
  const microcreditId = queryParams.get('microcredit_id');
  const data = useData();

  const required_data = ['plans'];

  useEffect(() => {
    data.handleLoaded('remove', required_data);
    data._get(required_data);
  }, [pathname]);

  const [isAnnual, setIsAnnual] = useState(false);
  const [planDetails, setPlanDetails] = useState({
    Free: { price: 0, features: [] },
    Standard: { price: 0, features: [] },
    Premium: { price: 0, features: [] },
    Gold: { price: 0, features: [] },
  });

  useEffect(() => {
    try {
      const details = {};
      data._plans.forEach((i) => {
        details[i.name] = i;
        details[i.name].price = data._cn(i.price).replaceAll(',00', '');
        details[i.name].features = (i.features || '')
          .replaceAll('\n', '')
          .split(';')
          .filter((i) => i);
      });
      setPlanDetails((prev) => ({ ...prev, ...details }));
    } catch (e) {
      // silent
    }
  }, [data._plans, pathname]);

  const getDisplayedPrice = (monthlyPrice) => {
   
    const price = parseFloat(String(monthlyPrice || 0).replace(/\./g, ""));
    if (isAnnual && price > 0) {
      return (price * 11).toFixed(2).replace(/\.?0+$/, '');
    }

    return `${price}`;
  };

  const navigateToCheckout = (plan) => {
    navigate(
      '/plan-checkout?plan=' +
        plan +
        '&period=' +
        (isAnnual ? 'yearly' : 'monthly') +
        (microcreditId ? '&microcredit_id=' + microcreditId : '')
    );
  };

  // ===== ICONS =====
  const CheckIcon = ({ className = '' }) => (
    <svg
      className={`w-3.5 h-3.5 ${className}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );

  // ===== STYLES =====
  const styles = `
    .plan-card {
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .plan-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.02);
    }
    .dark .plan-card:hover {
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3);
    }
    html { scroll-behavior: smooth; }
  `;

  // Order and metadata for each plan
  const planOrder = [
   // { key: 'Free', name: 'FREE', popular: false },
    { key: 'Standard', name: 'STANDARD', popular: false },
    { key: 'Premium', name: 'PREMIUM', popular: true },
    { key: 'Gold', name: 'GOLD', popular: false },
  ];

  return (
    <>
      <style>{styles}</style>
      <section
        id="plans"
        className="py-24 px-6 bg-white dark:bg-gray-950 transition-colors duration-300 font-sans"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-blue-700 dark:text-blue-500 font-bold uppercase tracking-widest text-sm mb-2">
              Planos
            </h3>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4">
              {microcreditId
                ? 'Seleccione um plano para reactivar o seu microcrédito'
                : 'Escolha o melhor plano para o seu negócio'}
            </h2>
          </div>

          {/* Loading state */}
          {!data._loaded.includes('plans') && (
            <div className="flex items-center justify-center gap-x-2 py-10">
              <span className="block text-gray-500 dark:text-gray-400">
                A carregar planos...
              </span>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-12">
            <div className="inline-flex items-center p-1.5 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setIsAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  !isAnnual
                    ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 !border-none'
                }`}
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setIsAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  isAnnual
                    ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 !border-none'
                }`}
              >
                Anual
                <span className="ml-1 text-xs text-green-600 dark:text-green-400">
                  - 1 mês de desconto
                </span>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          {data._loaded.includes('plans') && (
            <div className="grid md:grid-cols-3 gap-8">
              {planOrder.map(({ key, name, popular }) => {
                const plan = planDetails[key] || { price: 0, features: [] };
                return (
                  <div
                    key={key}
                    className={`plan-card bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-sm relative flex flex-col ${
                      popular
                        ? 'border-2 border-blue-600 dark:border-blue-500 shadow-xl transform md:scale-105'
                        : 'border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                        Mais Popular
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {name}
                    </h3>
                    <div className="mb-6">
                      <span className="text-4xl font-black text-gray-900 dark:text-white">
                        {getDisplayedPrice(plan.price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}MT
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {isAnnual ? '/ano' : '/mês'}
                      </span>
                    </div>
                    {isAnnual && (
                      <div className="text-sm text-green-600 dark:text-green-400 font-semibold mb-4">
                        1 mês grátis no pagamento anual
                      </div>
                    )}
                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-gray-600 dark:text-gray-300"
                        >
                          <CheckIcon className="text-blue-600 dark:text-blue-400 mt-1 shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigateToCheckout(key.toLowerCase())}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold transition-all"
                    >
                      {microcreditId ? 'Actualizar' : 'Requisitar'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400 hidden">
            Plano GRÁTIS disponível por 30 dias.{' '}
            <a
              href="#"
              className="text-blue-600 dark:text-blue-400 underline"
            >
              Saiba mais
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Plans;